import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Upload, KeyRound } from "lucide-react";
import { useI18n } from "@/lib/i18n";

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const { setLang } = useI18n();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(null);
  const [pw, setPw] = useState({ old_password: "", new_password: "", confirm: "" });

  useEffect(() => { if (user) setForm(user); }, [user]);

  const readAvatar = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => setForm({ ...form, avatar_b64: r.result });
    r.readAsDataURL(f);
  };

  const save = async () => {
    try {
      await api.put(`/users/${user.id}/profile`, {
        first_name: form.first_name, last_name: form.last_name, email: form.email,
        avatar_b64: form.avatar_b64, notification_prefs: form.notification_prefs, language: form.language,
      });
      toast.success("Profil aktualisiert");
      if (form.language) setLang(form.language);
      await refresh();
    } catch (err) { toast.error("Fehler"); }
  };

  const changePassword = async () => {
    if (pw.new_password !== pw.confirm) { toast.error("Passwörter stimmen nicht überein"); return; }
    try { await api.post("/auth/change-password", { old_password: pw.old_password, new_password: pw.new_password }); toast.success("Passwort geändert"); setPw({ old_password: "", new_password: "", confirm: "" }); }
    catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  if (!form) return null;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-root">
      <div>
        <div className="overline">Profil</div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold">Mein Profil</h2>
      </div>

      <Tabs defaultValue={searchParams.get("tab") === "password" ? "password" : "info"}>
        <TabsList>
          <TabsTrigger data-testid="profile-tab-info" value="info">Persönliche Daten</TabsTrigger>
          <TabsTrigger data-testid="profile-tab-notif" value="notif">Benachrichtigungen</TabsTrigger>
          <TabsTrigger data-testid="profile-tab-password" value="password">Passwort</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-4">
          <Card className="border-border/60"><CardContent className="pt-6 space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-heading font-extrabold text-2xl overflow-hidden">
                {form.avatar_b64 ? <img src={form.avatar_b64} alt="avatar" className="w-full h-full object-cover" /> : `${form.first_name?.[0] || ""}${form.last_name?.[0] || ""}`}
              </div>
              <div>
                <Label>Profilbild</Label>
                <Input type="file" accept="image/*" onChange={readAvatar} data-testid="profile-avatar" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Vorname</Label><Input data-testid="profile-firstname" value={form.first_name || ""} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
              <div><Label>Nachname</Label><Input data-testid="profile-lastname" value={form.last_name || ""} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
              <div className="col-span-2"><Label>E-Mail</Label><Input data-testid="profile-email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div>
                <Label>Sprache</Label>
                <Select value={form.language || "de"} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger data-testid="profile-lang"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="de">Deutsch</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="profile-save" onClick={save}><Save className="w-4 h-4 mr-2" /> Speichern</Button>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="notif" className="space-y-4">
          <Card className="border-border/60"><CardContent className="pt-6 space-y-4">
            <div className="flex items-center justify-between">
              <div><div className="font-medium">E-Mail-Benachrichtigungen</div><div className="text-sm text-slate-500">Master-Schalter für E-Mails</div></div>
              <Switch data-testid="notif-email" checked={form.notification_prefs?.email ?? true} onCheckedChange={(v) => setForm({ ...form, notification_prefs: { ...form.notification_prefs, email: v } })} />
            </div>
            <div className="flex items-center justify-between">
              <div><div className="font-medium">Push-Benachrichtigungen</div><div className="text-sm text-slate-500">Sofortige Meldungen im Browser</div></div>
              <Switch data-testid="notif-push" checked={form.notification_prefs?.push ?? true} onCheckedChange={(v) => setForm({ ...form, notification_prefs: { ...form.notification_prefs, push: v } })} />
            </div>
            <div className="pt-4 border-t border-border/60">
              <div className="text-sm font-medium mb-3">Ich möchte benachrichtigt werden über:</div>
              <div className="space-y-3">
                {[
                  ["absences", "Abwesenheits-Entscheidungen (Urlaub/Krankheit)"],
                  ["schedule_changes", "Änderungen im Dienstplan"],
                  ["announcements", "Neue Ankündigungen am Schwarzen Brett"],
                  ["tasks", "Neue Aufgaben & QM-Erinnerungen"],
                  ["handover", "Wichtige Übergabebuch-Einträge"],
                  ["chat", "Neue Chat-Nachrichten"],
                  ["overtime", "Überstunden-Warnungen"],
                ].map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div className="text-sm">{label}</div>
                    <Switch
                      data-testid={`notif-${key}`}
                      checked={form.notification_prefs?.[key] ?? true}
                      onCheckedChange={(v) => setForm({ ...form, notification_prefs: { ...form.notification_prefs, [key]: v } })}
                    />
                  </div>
                ))}
              </div>
            </div>
            <Button data-testid="profile-notif-save" onClick={save}><Save className="w-4 h-4 mr-2" /> Speichern</Button>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="password" className="space-y-4">
          <Card className="border-border/60"><CardContent className="pt-6 space-y-4">
            <div><Label>Altes Passwort</Label><Input data-testid="pw-old" type="password" value={pw.old_password} onChange={(e) => setPw({ ...pw, old_password: e.target.value })} /></div>
            <div><Label>Neues Passwort</Label><Input data-testid="pw-new" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></div>
            <div><Label>Bestätigen</Label><Input data-testid="pw-confirm" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} /></div>
            <Button data-testid="pw-save" onClick={changePassword}><KeyRound className="w-4 h-4 mr-2" /> Passwort ändern</Button>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
